/**
 * "Standorte": Bilder-Galerie unter "Routenregeln" (siehe Migration 0057).
 * Ansehen (inkl. Herunterladen) für alle Rollen mit "routenregeln.view"
 * (also auch "Routenpartner"). Hochladen/Umbenennen/Löschen ausschließlich
 * mit der Berechtigung "routenregeln.locations.manage" (ab Werk
 * ausschließlich "President").
 *
 * Die Bilddateien werden NICHT direkt per <img src="/api/..."> eingebunden,
 * da der Endpunkt hinter der normalen Bearer-Token-Authentifizierung liegt
 * (kein <img>-Tag kann eigene Header mitschicken). Stattdessen wird jedes
 * Bild einmalig per authentifiziertem fetch() geladen und als Object-URL
 * (URL.createObjectURL) angezeigt - dieselbe Object-URL dient auch als
 * Ziel für den "Herunterladen"-Link. Object-URLs werden beim Verlassen der
 * Seite/Neuladen der Liste wieder freigegeben (URL.revokeObjectURL).
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError, API_BASE, getToken } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card } from "../../components/ui/Card";
import { Modal } from "../../components/ui/Modal";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { formatDateTime } from "../../utils/formatDate";
import type { RouteRuleLocation } from "../../types";

async function fetchImageObjectUrl(id: number): Promise<string> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/api/route-rules/locations/${id}/file`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("Bild konnte nicht geladen werden.");
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export default function LocationsGallery() {
  const canManage = usePermission("routenregeln.locations.manage");
  const [locations, setLocations] = useState<RouteRuleLocation[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState<RouteRuleLocation | null>(null);
  const objectUrlsRef = useRef<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.get<RouteRuleLocation[]>("/api/route-rules/locations");
      setLocations(rows);

      // Alte Object-URLs freigeben, bevor neue geladen werden.
      objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      objectUrlsRef.current = [];

      const entries = await Promise.all(
        rows.map(async (row) => {
          try {
            const url = await fetchImageObjectUrl(row.id);
            objectUrlsRef.current.push(url);
            return [row.id, url] as const;
          } catch {
            return [row.id, null] as const;
          }
        })
      );
      setImageUrls(Object.fromEntries(entries.filter(([, url]) => url) as [number, string][]));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => { objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleDelete(location: RouteRuleLocation) {
    if (!confirm(`Bild${location.label ? ` "${location.label}"` : ""} wirklich löschen?`)) return;
    try {
      await api.delete(`/api/route-rules/locations/${location.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <div className="space-y-4">
      {canManage && <UploadForm onUploaded={load} />}

      {loading ? (
        <Spinner />
      ) : locations.length === 0 ? (
        <Card className="py-10">
          <EmptyState message="Noch keine Standorte-Bilder hochgeladen." />
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((location) => (
            <Card key={location.id} padded={false} className="flex flex-col overflow-hidden">
              <div className="aspect-video bg-black/30 flex items-center justify-center overflow-hidden">
                {imageUrls[location.id] ? (
                  <img src={imageUrls[location.id]} alt={location.label ?? "Standort"} className="w-full h-full object-cover" />
                ) : (
                  <Spinner />
                )}
              </div>
              <div className="p-3 flex flex-col gap-2 flex-1">
                <p className="font-medium text-gray-100 truncate">{location.label ?? "Ohne Bezeichnung"}</p>
                <p className="text-xs text-gray-500">
                  {formatDateTime(location.created_at)}
                  {location.created_by_name && <span className="block text-gray-600">von {location.created_by_name}</span>}
                </p>
                <div className="flex items-center justify-between mt-auto pt-2 border-t border-surface-border">
                  {imageUrls[location.id] ? (
                    <a
                      href={imageUrls[location.id]}
                      download={`${location.label || "standort"}.${location.content_type.split("/")[1] ?? "jpg"}`}
                      className="text-accent text-xs hover:underline"
                    >
                      Herunterladen
                    </a>
                  ) : <span />}
                  {canManage && (
                    <div className="flex gap-3">
                      <button className="text-accent text-xs hover:underline" onClick={() => setRenaming(location)}>Umbenennen</button>
                      <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(location)}>Löschen</button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {renaming && (
        <RenameModal location={renaming} onClose={() => setRenaming(null)} onSaved={() => { setRenaming(null); load(); }} />
      )}
    </div>
  );
}

function UploadForm({ onUploaded }: { onUploaded: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!file) { setError("Bitte ein Bild auswählen."); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (label.trim()) formData.append("label", label.trim());
      await api.upload("/api/route-rules/locations", formData);
      setFile(null);
      setLabel("");
      if (inputRef.current) inputRef.current.value = "";
      onUploaded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Hochladen fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-3">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">Bild</label>
            <input
              ref={inputRef}
              className="input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div>
            <label className="label">Bezeichnung (optional)</label>
            <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="z.B. Lagerhalle Nord" />
          </div>
        </div>
        <div className="flex justify-end">
          <button className="btn-primary" disabled={submitting || !file}>{submitting ? "Wird hochgeladen…" : "Hochladen"}</button>
        </div>
      </form>
    </Card>
  );
}

function RenameModal({ location, onClose, onSaved }: { location: RouteRuleLocation; onClose: () => void; onSaved: () => void }) {
  const [label, setLabel] = useState(location.label ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.put(`/api/route-rules/locations/${location.id}`, { label: label.trim() || undefined });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Bild umbenennen"
      onClose={onClose}
      size="sm"
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Abbrechen</button>
        <button className="btn-primary" form="location-rename-form" disabled={submitting}>{submitting ? "Speichern…" : "Speichern"}</button>
      </>}
    >
      <form id="location-rename-form" onSubmit={handleSubmit} className="space-y-3">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Bezeichnung</label>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
        </div>
      </form>
    </Modal>
  );
}
